import { createLazyResource, createMutation, createTriggerResource, toast, useLocalization } from '@magicdoorinc/ui';
import { createMemo } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { letterTemplateRepository } from '~/repositories/letterTemplateRepository';
import type {
  CommunicationChannel,
  CreateLetterTemplateDto,
  LetterTemplateDto,
  PaginationDto,
  PlaceholderDefinitionsDto,
  UpdateLetterTemplateDto,
} from '~/repositories/letterTemplateRepository';

export const [LetterTemplateProvider, useLetterTemplate] = createMagicDoorContext('LetterTemplate', () => {
  const { t } = useLocalization();

  const [allTemplates, { refetch, mutate }] = createLazyResource<PaginationDto<LetterTemplateDto>>(() =>
    letterTemplateRepository.getTemplates({ pageSize: 100 })
  );

  const [current, setCurrentId, { mutate: mutateCurrent }] = createTriggerResource((id: string) =>
    letterTemplateRepository.getTemplate(id)
  );

  const [placeholders, setPlaceholderChannels] = createTriggerResource<CommunicationChannel[], PlaceholderDefinitionsDto>(
    (channels: CommunicationChannel[]) => letterTemplateRepository.getPlaceholders({ channels })
  );

  const templatesByGroup = createMemo(() => {
    const items = allTemplates()?.items ?? [];
    const grouped = new Map<string | null, LetterTemplateDto[]>();

    for (const template of items) {
      const key = template.letterTemplateGroupId ?? null;
      const templates = grouped.get(key);
      if (templates) {
        templates.push(template);
      } else {
        grouped.set(key, [template]);
      }
    }

    return grouped;
  });

  const getTemplatesForGroup = (groupId: string | null) => {
    return templatesByGroup().get(groupId) ?? [];
  };

  const ungroupedTemplates = createMemo(() => getTemplatesForGroup(null));

  const addTemplate = createMutation(async (payload: CreateLetterTemplateDto) => {
    const result = await letterTemplateRepository.createTemplate(payload);
    toast.success(t('{name} has been created successfully', { name: t('Template') }));
    refetch();
    return result;
  });

  const updateTemplate = createMutation(async ({ id, payload }: { id: string; payload: UpdateLetterTemplateDto }) => {
    const result = await letterTemplateRepository.updateTemplate(id, payload);
    toast.success(t('{name} has been updated successfully', { name: t('Template') }));
    refetch();
    mutateCurrent(result);
    return result;
  });

  const deleteTemplate = createMutation(async (id: string) => {
    await letterTemplateRepository.deleteTemplate(id);
    toast.success(t('{name} has been deleted successfully', { name: t('Template') }));
    mutate(
      (previous) =>
        previous && {
          ...previous,
          items: previous.items.filter((template) => template.id !== id),
        }
    );
  });

  const moveToGroup = createMutation(async ({ id, groupId }: { id: string; groupId: string | null }) => {
    const result = await letterTemplateRepository.updateTemplate(id, {
      letterTemplateGroupId: groupId,
    });
    toast.success(t('{name} has been moved successfully', { name: t('Template') }));
    refetch();
    return result;
  });

  const validatePlaceholders = async (content: string, channels: CommunicationChannel[]) => {
    return letterTemplateRepository.validatePlaceholders({
      content,
      communicationChannels: channels,
    });
  };

  return {
    allTemplates,
    current,
    setCurrentId,
    placeholders,
    setPlaceholderChannels,
    templatesByGroup,
    getTemplatesForGroup,
    ungroupedTemplates,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    moveToGroup,
    validatePlaceholders,
    refetch,
  };
});
